import {useEffect, useMemo} from 'react';
import ReactECharts from 'echarts-for-react';

// ---------------------------------------------------------------------------
// Deterministic PRNG helpers (mulberry32)
// ---------------------------------------------------------------------------

function mulberry32(a: number): () => number {
    return () => {
        let t = (a += 0x6d2b79f5);
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

function seedToInt(seed: string | undefined | null, status: string): number {
    const seedStr = seed == null ? `${status}` : `${seed}-${status}`;
    return seedStr.split('').reduce((acc, ch) => (acc * 31 + ch.charCodeAt(0)) >>> 0, 2166136261);
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MiniSparklineProps {
    status: 'good' | 'critical' | 'uptrend' | string;
    seed: string;
    /** 6-element numeric array; null entries are skipped — only valid numbers are drawn. */
    actualData?: (number)[] | null;
    planData?: (number)[] | null;
    /** Legend labels identifying the data source. */
    actualLabel?: string;
    planLabel?: string;
    title?: string;
}

// ---------------------------------------------------------------------------
// Chart constants
// ---------------------------------------------------------------------------

const WIDTH = 80;
const HEIGHT = 40;
const PADDING = 4;

const COLOR_MAP: Record<string, string> = {
    good: '#1e293b',
    uptrend: '#1e293b',
    critical: '#dc2626',
};

const FALLBACK_COLOR = '#64748b';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Strip null entries from an array, preserving only valid numbers. */
function dropNulls(arr: (number)[]): number[] {
    //0 也算空值
    // return arr.filter((v): v is number => v != null || v != 0);
    return arr || []
}

/** Normalise values into the 10–50 display range. */
function normalise(values: number[], rawMin: number, rawMax: number): number[] {
    const rawRange = rawMax - rawMin || 1;
    return values.map((v) => 10 + ((v - rawMin) / rawRange) * 40);
}

/** Generate synthetic series when real data is absent. */
function generateSeries(seed: string, status: string): { actual: number[]; plan: number[] } {
    const rand = mulberry32(seedToInt(seed, status));
    const base = rand() * 30 + 20;
    const trend = status === 'uptrend' ? 1.15 : status === 'good' ? 1.1 : status === 'critical' ? 0.9 : 1.0;

    const actual: number[] = [];
    let value = base;
    for (let i = 0; i < 6; i++) {
        if (status === 'uptrend') {
            value = value * (1.08 + rand() * 0.1);
        } else {
            value = value * (trend + (rand() - 0.5) * 0.3);
        }
        actual.push(Math.max(10, Math.min(50, value)));
    }

    const plan = actual.map((v) => {
        const noise = (rand() - 0.5) * 0.12;
        const drift = status === 'critical' ? 1.06 : status === 'good' || status === 'uptrend' ? 0.98 : 1.0;
        return Math.max(10, Math.min(50, v * (drift + noise)));
    });

    return {actual, plan};
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function MiniSparkline(
    {
        status,
        seed,
        actualData,
        planData,
        actualLabel = 'Act',
        planLabel = 'Plan',
        title = 'IMS trend - Past 6 months'
    }: MiniSparklineProps) {

    const series = useMemo(() => {
        // Use real data when at least one value is present
        if (Array.isArray(actualData) && actualData.length === 6 && actualData.some((v) => v != null)) {
            const filteredActual = dropNulls(actualData);

            let filteredPlan: number[];
            if (Array.isArray(planData) && planData.length === 6 && planData.some((v) => v != null)) {
                filteredPlan = dropNulls(planData);
            } else {
                const rand = mulberry32(seedToInt(seed, status));
                filteredPlan = filteredActual.map((v) => {
                    const noise = (rand() - 0.5) * 0.12;
                    const drift = status === 'critical' ? 1.06 : status === 'good' || status === 'uptrend' ? 0.98 : 1.0;
                    return v * (drift + noise);
                });
            }

            const allRaw = [...filteredActual, ...filteredPlan];
            const rawMin = Math.min(...allRaw.filter((v) => v > 0));
            const rawMax = Math.max(...allRaw);
            return {
                actual: normalise(filteredActual, rawMin, rawMax),
                plan: normalise(filteredPlan, rawMin, rawMax),
            };
        }

        return generateSeries(seed, status);
    }, [status, seed, actualData, planData]);

    const allValues = [...series.actual, ...series.plan];
    const min = Math.min(...allValues);
    const max = Math.max(...allValues);
    const range = max - min || 1;

    const getPoints = (arr: number[]) =>
        arr.length === 0
            ? ''
            : arr
                .map((v, i) => {
                    const x = arr.length === 1
                        ? WIDTH / 2
                        : PADDING + (i / (arr.length - 1)) * (WIDTH - PADDING * 2);
                    const y = HEIGHT - PADDING - ((v - min) / range) * (HEIGHT - PADDING * 2);
                    return `${x},${y}`;
                })
                .join(' ');

    const color = COLOR_MAP[status] ?? FALLBACK_COLOR;

    const lastActualIdx = series.actual.length - 1;
    const lastY = HEIGHT - PADDING - ((series.actual[lastActualIdx] - min) / range) * (HEIGHT - PADDING * 2);
    const lastX = series.actual.length === 1
        ? WIDTH / 2
        : PADDING + (lastActualIdx / (series.actual.length - 1)) * (WIDTH - PADDING * 2);

    return (
        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0}}>
            <div style={{
                fontSize: 9,
                fontWeight: 600,
                color: "#475569",
                marginBottom: 3,
                textAlign: "center",
                whiteSpace: "nowrap"
            }}>{title}</div>
            {/*<div id={"echarts-container"} style={{flexShrink: 0, width: WIDTH, height: HEIGHT}}>*/}
            {/*    <ReactECharts option={option} style={{width: '100%', height: '100%'}}/>*/}
            {/*</div>*/}
            <svg width={WIDTH} height={HEIGHT} style={{flexShrink: 0}}>
                {/* Plan line (dashed) */}
                <polyline
                    points={getPoints(series.plan)}
                    fill="none"
                    stroke={color}
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeDasharray="4 3"
                    opacity={0.65}
                />
                {/* Actual line (solid) */}
                <polyline
                    points={getPoints(series.actual)}
                    fill="none"
                    stroke={color}
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                {/* Endpoint dot */}
                <circle cx={lastX} cy={lastY} r={3} fill={color}/>
            </svg>

            {/* Legend */}
            <div style={{
                display: 'flex',
                gap: 8,
                marginTop: 4,
                fontSize: 8,
                color: '#94a3b8',
                fontWeight: 500,
            }}>
        <span style={{display: 'flex', alignItems: 'center', gap: 3}}>
          <svg width="12" height="2" viewBox="0 0 12 2">
            <line x1="0" y1="1" x2="12" y2="1" stroke={color} strokeWidth="2" strokeLinecap="round"/>
          </svg>
            {actualLabel}
        </span>
                <span style={{display: 'flex', alignItems: 'center', gap: 3}}>
          <svg width="12" height="2" viewBox="0 0 12 2">
            <line x1="0" y1="1" x2="12" y2="1" stroke={color} strokeWidth="2" strokeDasharray="1 3"
                  strokeLinecap="round"/>
          </svg>
                    {planLabel}
        </span>
            </div>
        </div>
    );
}
